import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, RefreshCcw, Copy, Link2 } from 'lucide-react';
import { telegramAPI } from '../api/client';
import { useLanguage } from '../context/LanguageContext';
import { useLevel } from '../context/LevelContext';
import { useAuth } from '../context/AuthContext';
import { buildLevelPath } from '../utils/routeLanguage';

export const TelegramLinkPage = () => {
  const { language } = useLanguage();
  const { selectedLevel } = useLevel();
  const { user } = useAuth();
  const navigate = useNavigate();
  const levelPath = buildLevelPath(language, selectedLevel);
  const isStaff = user?.role === 'admin' || user?.role === 'teacher';

  const [status, setStatus] = useState(null);
  const [codeData, setCodeData] = useState(null);
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [loadingCode, setLoadingCode] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (user?.role && isStaff) {
      navigate(levelPath, { replace: true });
      return;
    }

    const loadStatus = async () => {
      try {
        setLoadingStatus(true);
        const response = await telegramAPI.getStatus();
        setStatus(response.data);
      } catch (err) {
        setError(err.response?.data?.error || 'Не удалось загрузить статус привязки.');
      } finally {
        setLoadingStatus(false);
      }
    };

    loadStatus();
  }, [user?.role, isStaff, navigate, levelPath]);

  const generateCode = async () => {
    try {
      setError('');
      setLoadingCode(true);
      const response = await telegramAPI.generateLinkCode();
      setCodeData(response.data);
      const statusResponse = await telegramAPI.getStatus();
      setStatus(statusResponse.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Не удалось получить одноразовый код.');
    } finally {
      setLoadingCode(false);
    }
  };

  const copyCommand = async () => {
    if (!codeData?.code) {
      return;
    }

    const command = `${codeData.code}`;
    try {
      await navigator.clipboard.writeText(command);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      setError('Не удалось скопировать команду. Скопируй вручную.');
    }
  };

  return (
    <div className="ticket-page">
      <div className="ticket-container">
        <button onClick={() => navigate(levelPath)} className="ticket-back-btn">
          <ArrowLeft size={16} />
          Назад
        </button>

        <div className="ticket-card">
          <h1 className="ticket-page-title" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Link2 size={26} /> Привязка Telegram
          </h1>
          <p className="ticket-page-desc">
            Получи одноразовый код и просто отправь боту эти цифры одним сообщением.
          </p>

          {error && <div className="alert alert-error" style={{ marginBottom: 16 }}>{error}</div>}

          <div className="concept-card rounded-lg p-4 mb-4">
            {loadingStatus ? (
              <p className="text-muted">Загружаю статус...</p>
            ) : (
              <>
                <p className="text-white font-bold mb-1">
                  Статус: {status?.linked ? 'Telegram привязан' : 'Telegram не привязан'}
                </p>
                <p className="text-muted text-sm mb-1">
                  Доступ к боту: {status?.telegramBotEnabled ? 'включен' : 'выключен'}
                </p>
                {status?.telegramUsername && (
                  <p className="text-muted text-sm">Username: @{status.telegramUsername}</p>
                )}
              </>
            )}
          </div>

          <button
            type="button"
            onClick={generateCode}
            disabled={loadingCode}
            className="modern-button-primary"
            style={{ width: '100%', marginBottom: 16 }}
          >
            {loadingCode ? 'Генерирую код...' : 'Получить одноразовый код'}
          </button>

          {codeData?.code && (
            <div className="concept-card rounded-lg p-4">
              <p className="text-muted text-sm mb-2">Твой код:</p>
              <div className="text-white text-2xl font-bold tracking-widest mb-3">{codeData.code}</div>
              <p className="text-muted text-sm mb-3">
                Действует до: {new Date(codeData.expiresAt).toLocaleString('ru-RU')}
              </p>
              <div className="flex gap-2" style={{ flexWrap: 'wrap' }}>
                <button
                  type="button"
                  onClick={copyCommand}
                  className="px-4 py-2 bg-card border border-border hover:border-coral text-muted rounded-lg font-bold transition"
                >
                  <Copy size={14} style={{ display: 'inline', marginRight: 6 }} />
                  {copied ? 'Скопировано' : 'Скопировать код'}
                </button>
                <button
                  type="button"
                  onClick={generateCode}
                  disabled={loadingCode}
                  className="px-4 py-2 bg-card border border-border hover:border-gold text-muted rounded-lg font-bold transition"
                >
                  <RefreshCcw size={14} style={{ display: 'inline', marginRight: 6 }} />
                  Обновить код
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
